import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { AdminLayout } from "@/pages/admin/AdminLayout";
import { timeAgo } from "@/lib/format";

const STAT_META = [
  ["published", "Published", "text-emerald"],
  ["awaiting_review", "Awaiting Review", "text-amber"],
  ["draft", "Drafts", "text-cyan"],
  ["candidates", "Candidate Stories", "text-crimson"],
  ["scheduled", "Scheduled", "text-admtext2"],
  ["companies", "Companies", "text-admtext2"],
  ["sources", "Sources", "text-admtext2"],
  ["subscribers", "Subscribers", "text-admtext2"],
];

export default function Dashboard() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then((r) => setData(r.data)).catch(() => {}); }, []);
  const counts = data?.counts || {};
  return (
    <AdminLayout title="Dashboard">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {STAT_META.map(([key, label, color]) => (
          <div key={key} className="bg-admcard border border-admborder p-4" data-testid={`stat-${key}`}>
            <div className={`font-mono text-3xl font-medium ${color}`}>{counts[key] ?? "—"}</div>
            <div className="text-xs text-admtext2 uppercase tracking-wider mt-1">{label}</div>
          </div>
        ))}
      </div>

      <div className="mt-6 bg-admcard border border-admborder">
        <div className="px-5 py-3 border-b border-admborder flex items-center justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-admtext2">Recent Activity</h2>
          <Link to="/admin/articles" className="text-xs text-cyan">All articles →</Link>
        </div>
        <div>
          {(data?.recent || []).map((a) => (
            <Link key={a.id} to={`/admin/articles/${a.id}`} className="flex items-center justify-between px-5 py-3 border-b border-admborder last:border-0 hover:bg-admcardh transition-colors">
              <div className="min-w-0">
                <div className="text-sm text-admtext truncate">{a.headline}</div>
                <div className="text-xs text-admmuted font-mono mt-0.5">{a.category} · {timeAgo(a.created_at)}</div>
              </div>
              <StatusPill status={a.status} />
            </Link>
          ))}
          {!data?.recent?.length && <div className="px-5 py-6 text-admmuted text-sm">No articles yet.</div>}
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <Link to="/admin/ai-desk" className="bg-gradient-to-br from-admcard to-admcardh border border-admborder p-6 hover:border-cyan transition-colors" data-testid="dash-ai-desk">
          <div className="text-cyan text-xs uppercase tracking-widest font-bold">AI Editorial Desk</div>
          <h3 className="font-serif text-2xl mt-2 text-admtext">The Fin Desk Editor</h3>
          <p className="text-sm text-admtext2 mt-1">Run autonomous research, review candidate stories and manage the editorial pipeline.</p>
        </Link>
        <Link to="/admin/candidates" className="bg-admcard border border-admborder p-6 hover:border-crimson transition-colors" data-testid="dash-candidates">
          <div className="text-crimson text-xs uppercase tracking-widest font-bold">Candidate Stories</div>
          <h3 className="font-serif text-2xl mt-2 text-admtext">{counts.candidates ?? 0} awaiting triage</h3>
          <p className="text-sm text-admtext2 mt-1">Review discovered developments and turn them into drafts.</p>
        </Link>
      </div>
    </AdminLayout>
  );
}

export function StatusPill({ status }) {
  const map = {
    published: "bg-emerald/15 text-emerald", awaiting_review: "bg-amber/15 text-amber",
    draft: "bg-cyan/15 text-cyan", scheduled: "bg-admtext2/15 text-admtext2",
    archived: "bg-admmuted/15 text-admmuted",
  };
  return <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 shrink-0 ${map[status] || "bg-admborder text-admtext2"}`}>{(status || "").replace("_", " ")}</span>;
}
